import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight, Navigation, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { ServiceAreaMap } from "@/components/common/ServiceAreaMap";
import { Button } from "@/components/ui/button";
import { business, serviceRegions, vaMedicalCenters } from "@/data/site";

const STATE_NAMES = {
  PA: "Pennsylvania",
  MD: "Maryland",
  WV: "West Virginia",
  VA: "Virginia",
};

const vaByState = ["PA", "MD", "WV", "VA"]
  .map((code) => ({
    code,
    name: STATE_NAMES[code],
    centers: vaMedicalCenters.filter((v) => v.state === code && v.inRadius),
  }))
  .filter((g) => g.centers.length > 0);

export default function ServiceArea() {
  return (
    <div>
      <PageHeader
        eyebrow="Service area"
        title="Local coverage &amp; VA locations"
        description="We provide in-home delivery, setup and repair across Central Pennsylvania and the surrounding Tri-State region — and we work in and around the area VA Medical Centers."
      >
        <Button asChild variant="brand" size="lg">
          <a href={business.phoneHref}><Phone className="h-4 w-4" /> Call {business.phone}</a>
        </Button>
      </PageHeader>

      {/* ===== MAP ===== */}
      <section className="section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading
            eyebrow="Coverage map"
            title="Our ~2.5-hour service radius around Johnstown"
            description="The dashed gold ring marks roughly 2.5 hours of driving from Johnstown, PA. Gold pins are the counties we serve; green pins are the VA Medical Centers within that radius."
          />
          <Reveal className="mt-12">
            <ServiceAreaMap />
          </Reveal>
        </div>
      </section>

      {/* ===== REGIONS ===== */}
      <section className="bg-muted section-py">
        <div className="mx-auto max-w-7xl container-px">
          <div className="grid gap-6 lg:grid-cols-2">
            {serviceRegions.map((region, i) => (
              <Reveal key={region.name} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-3xl border border-border bg-card p-8 shadow-soft">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                      <Navigation className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-foreground">{region.name}</h2>
                      <p className="text-sm text-accent">{region.note}</p>
                    </div>
                  </div>
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {region.areas.map((a) => (
                      <li key={a} className="flex items-center gap-2.5 rounded-xl border border-border bg-muted/40 px-4 py-3 text-[15px] font-medium text-foreground">
                        <MapPin className="h-5 w-5 shrink-0 text-primary" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VA MEDICAL CENTERS ===== */}
      <section className="section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading
            eyebrow="For our Veterans"
            title="VA Medical Centers within reach"
            description="VA Medical Centers located within about 2.5 hours of Johnstown, PA."
          />
          <div className="mx-auto mt-12 grid max-w-3xl gap-6 sm:grid-cols-2">
            {vaByState.map((group, i) => (
              <Reveal key={group.code} delay={(i % 4) * 0.06}>
                <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-accent" />
                    <h3 className="text-lg font-bold text-foreground">{group.name}</h3>
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {group.centers.map((c) => (
                      <li key={c.name} className="flex items-start gap-2 text-[15px] leading-snug text-muted-foreground">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /> {c.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-muted pb-20">
        <div className="mx-auto max-w-7xl container-px">
          <Reveal>
            <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-soft sm:p-12">
              <h3 className="text-2xl font-bold text-foreground">Not sure if you&apos;re in our area?</h3>
              <p className="mx-auto mt-3 max-w-xl text-[15px] text-muted-foreground">
                We&apos;ll always do our best to help. Give us a call or send a request and we&apos;ll let
                you know right away.
              </p>
              <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild variant="brand" size="lg">
                  <a href={business.phoneHref}><Phone className="h-4 w-4" /> {business.phone}</a>
                </Button>
                <Button asChild variant="outlineDark" size="lg">
                  <Link to="/contact">Request service <ArrowRight className="h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
